import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { FeedAuthor } from "@gooner/shared";
import { formatWhen } from "../lib/format";
import { Avatar } from "./Avatar";
import { GoldName } from "./Gold";
import { cx } from "./primitives";
import { MASK } from "../../lib/replayMask";

/**
 * One line somebody said: their face, their name (a link to their page), when, and the
 * body — the Feed's comment row lifted out (2026-09-19) so a Goon Room's chat is the
 * same row rather than a second drawing of it. `actions` sit on the name line (Reply,
 * Delete); `indent` is a reply's step in.
 *
 * `profileInNewTab`: a room must not navigate away — its socket and its camera die with
 * the page — so there the name opens the profile beside it rather than in place.
 * `onNameClick` makes the name a button instead (a room's host gets their menu off it);
 * `nameMenu` is drawn under the name line, anchored to it. `tag` is a small grey word
 * right after the name — a room's "(Host)".
 */
export function MessageRow({
  author,
  at,
  children,
  actions,
  indent,
  profileInNewTab,
  onNameClick,
  nameMenu,
  tag,
}: {
  author: FeedAuthor;
  at: number;
  children: ReactNode;
  actions?: ReactNode;
  indent?: boolean;
  profileInNewTab?: boolean;
  onNameClick?: () => void;
  nameMenu?: ReactNode;
  tag?: string;
}) {
  const nameClass = cx("font-medium hover:underline", author.gold ? "text-[var(--color-accent-soft)]" : "text-zinc-100");
  const href = `/u/${encodeURIComponent(author.username)}`;
  // Session replay: the name element carries the marker so its `href=/u/<name>` and
  // `aria-label` are masked with it (analytics.ts maskReplayAttribute); the body below is
  // what they said. Reply / Delete / the time stay readable.
  return (
    <div className={cx("flex gap-2", indent && "ml-8")}>
      <Avatar name={author.username} src={author.avatarUrl} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="relative flex items-baseline gap-2 text-xs">
          {onNameClick ? (
            <button type="button" onClick={onNameClick} className={nameClass} aria-label={`${author.username} — options`} {...MASK}>
              <GoldName name={author.username} gold={author.gold} />
            </button>
          ) : profileInNewTab ? (
            <a href={href} target="_blank" rel="noopener" className={nameClass} {...MASK}>
              <GoldName name={author.username} gold={author.gold} />
            </a>
          ) : (
            <Link to={href} className={nameClass} {...MASK}>
              <GoldName name={author.username} gold={author.gold} />
            </Link>
          )}
          {tag ? <span className="text-[11px] text-zinc-500">{tag}</span> : null}
          <span className="text-zinc-600">{formatWhen(at)}</span>
          {actions}
          {nameMenu}
        </div>
        {children}
      </div>
    </div>
  );
}
